import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, DownloadSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getToken, BACKEND_URL, copyToClipboard } from "../lib/api";

/**
 * Import dialog — provides a personalised bookmarklet the user drags to their bookmarks bar.
 * The bookmarklet loads /trika-import.js from Trika and passes their auth token in.
 */
export const ImportChatDialog = ({ onClose }) => {
  const [copied, setCopied] = useState(false);
  const anchorRef = useRef(null);
  const token = getToken() || "";
  const api = `${BACKEND_URL}/api`;

  // The bookmarklet payload — small stub that loads the extractor with user's token pre-injected.
  const bookmarklet =
    "javascript:(function(){" +
    `window.__TRIKA_TOKEN__=${JSON.stringify(token)};` +
    `window.__TRIKA_API__=${JSON.stringify(api)};` +
    "var s=document.createElement('script');" +
    `s.src=${JSON.stringify(`${BACKEND_URL}/trika-import.js?v=` + Date.now())};` +
    "s.onerror=function(){alert('Trika: could not load the importer script. Check your connection and try again.')};" +
    "document.body.appendChild(s);" +
    "})();";

  // React sanitises `javascript:` hrefs. Set it directly via the DOM to keep drag-to-bookmark working.
  useEffect(() => {
    if (anchorRef.current) anchorRef.current.setAttribute("href", bookmarklet);
  }, [bookmarklet]);

  const copyCode = async () => {
    const ok = await copyToClipboard(bookmarklet);
    if (ok) { setCopied(true); toast.success("Copied — paste into a new bookmark's URL field"); setTimeout(() => setCopied(false), 2500); }
    else toast.error("Could not copy — select manually and copy");
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 scale-in"
      data-testid="import-chat-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-[var(--surface)] border border-[var(--border-strong)] rounded-3xl p-7 relative"
        style={{ boxShadow: "var(--shadow)" }} onClick={(e) => e.stopPropagation()}>
        <button data-testid="import-chat-close" onClick={onClose} title="Close"
          className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--text)] p-2 rounded-full">
          <X size={18} />
        </button>

        <h2 className="font-serif-display font-semibold text-2xl mb-1">Import from Claude or ChatGPT</h2>
        <p className="text-sm text-[var(--text-2)] mb-5">
          One-time setup — after that, any conversation (public share <em>or</em> your own private chat) imports with a single click.
        </p>

        <ol className="space-y-4 text-[13.5px] text-[var(--text)]">
          <li>
            <span className="font-semibold">1.</span> Make sure your browser&apos;s bookmarks bar is visible.
          <span className="block text-[12px] text-[var(--muted)] mt-0.5">Chrome / Edge: <kbd className="px-1 rounded bg-[var(--surface-2)] text-[11px]">⌘⇧B</kbd> (Mac) or <kbd className="px-1 rounded bg-[var(--surface-2)] text-[11px]">Ctrl+Shift+B</kbd> (Win).</span>
          </li>
          <li>
            <span className="font-semibold">2.</span> Drag this button up to your bookmarks bar:
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <a
                ref={anchorRef}
                href="#trika-import"
                onClick={(e) => e.preventDefault()}
                data-testid="import-bookmarklet-anchor"
                draggable
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold text-[13px] cursor-grab active:cursor-grabbing select-none"
                style={{ background: "var(--accent)" }}
                title="Drag me to your bookmarks bar"
              >
                <DownloadSimple size={15} weight="bold" /> Import to Trika
              </a>
              <button data-testid="import-copy-code" onClick={copyCode}
                className="text-[12px] font-medium text-[var(--text-2)] hover:text-[var(--text)] underline underline-offset-4 decoration-dotted">
                {copied ? "Copied ✓" : "or copy the code"}
              </button>
            </div>
          </li>
          <li>
            <span className="font-semibold">3.</span> Open any conversation on{" "}
            <a href="https://claude.ai" target="_blank" rel="noreferrer" className="underline decoration-dotted underline-offset-4">claude.ai</a>{" "}
            or{" "}
            <a href="https://chatgpt.com" target="_blank" rel="noreferrer" className="underline decoration-dotted underline-offset-4">chatgpt.com</a>{" "}
            (public share links or your own private chats) and click the <span className="font-semibold">Import to Trika</span> bookmark.
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0" style={{ color: "var(--accent)" }} />
            <span className="text-[var(--text-2)]">The conversation is saved to your workspace, opens in a new tab, and is ready to continue with any model.</span>
          </li>
        </ol>

        <p className="text-[11px] text-[var(--muted)] mt-6 leading-relaxed">
          Privacy: the bookmarklet only runs when you click it. It reads only the chat page you&apos;re viewing and sends it directly to Trika using your session token. No third-party servers.
        </p>
      </div>
    </div>,
    document.body
  );
};
